// useAuthFlow — a thin React binding over the SDK's headless auth machine.
//
// `connect()` is async, so the flow isn't available on the first render; once
// it is, we subscribe and mirror its state into React state so the component
// re-renders on every transition. The machine itself is framework-agnostic
// (the vanilla test app drives the SAME `flow()` with `.subscribe()`); this
// hook is the ~15 lines a React app needs, nothing more.

import { useEffect, useState } from "react";
import type { AuthFlow, AuthFlowState } from "@s3p/sdk";
import { connect } from "/sdk/index.js";

export function useAuthFlow(): { flow: AuthFlow | null; state: AuthFlowState | null } {
  const [flow, setFlow] = useState<AuthFlow | null>(null);
  const [state, setState] = useState<AuthFlowState | null>(null);

  useEffect(() => {
    let unsub = () => {};
    let alive = true;
    void (async () => {
      const app = await connect();
      if (!alive) return;
      const f = app.auth.flow();
      setFlow(f);
      unsub = f.subscribe(setState); // fires immediately with the current state
    })();
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  return { flow, state };
}
